import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";

interface SubcontractorAssignment {
  _id: Id<"locationSubcontractorAssignments">;
  subcontractorId: Id<"subcontractors">;
  subcontractorName: string;
  contactName: string;
  email: string;
  phone: string;
  services: string[];
  status: "active" | "completed" | "scheduled";
  startDate?: string;
  endDate?: string;
  notes?: string;
  assignedAt: number;
}

interface EditSubcontractorAssignmentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: SubcontractorAssignment | null;
  onAssignmentUpdated?: () => void;
}

export function EditSubcontractorAssignmentSheet({
  isOpen,
  onClose,
  assignment,
  onAssignmentUpdated,
}: EditSubcontractorAssignmentSheetProps) {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [status, setStatus] = useState<string>("active");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch full subcontractor details to get available services
  const subcontractor = useQuery(
    api.subcontractors.getSubcontractorById,
    assignment ? { id: assignment.subcontractorId } : "skip"
  );

  // Update assignment mutation
  const updateAssignment = useMutation(api.serviceLocations.updateSubcontractorAssignment);

  // Get available services from subcontractor
  const availableServices = subcontractor?.services ?? [];

  // Initialize form data when assignment changes
  useEffect(() => {
    if (assignment) {
      setSelectedServices(assignment.services);
      setStatus(assignment.status);
      setStartDate(assignment.startDate || "");
      setEndDate(assignment.endDate || "");
      setNotes(assignment.notes || "");
    }
  }, [assignment]);

  // Handle service selection
  const handleServiceToggle = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!assignment) return;

    if (selectedServices.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one service",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAssignment({
        assignmentId: assignment._id,
        services: selectedServices,
        status: status as "active" | "completed" | "scheduled",
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        notes: notes || undefined,
      });

      toast({
        title: "Success!",
        description: "Assignment updated successfully",
      });

      // Call the callback to refresh data
      if (onAssignmentUpdated) {
        onAssignmentUpdated();
      }

      onClose();
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast({
        title: "Error",
        description: "Failed to update assignment",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when sheet closes
  const handleClose = () => {
    if (assignment) {
      setSelectedServices(assignment.services);
      setStatus(assignment.status);
      setStartDate(assignment.startDate || "");
      setEndDate(assignment.endDate || "");
      setNotes(assignment.notes || "");
    }
    onClose();
  };

  if (!assignment) return null;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Assignment</SheetTitle>
          <SheetDescription>
            Update assignment details for {assignment.subcontractorName}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 px-2 py-6">
            {/* Company Information (Read-only) */}
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <h3 className="font-medium text-gray-900">Company Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Company:</span>
                  <p>{assignment.subcontractorName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Contact:</span>
                  <p>{assignment.contactName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Email:</span>
                  <p className="text-blue-600">{assignment.email}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Phone:</span>
                  <p className="text-blue-600">{assignment.phone}</p>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="space-y-2">
              <Label>
                Services <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {availableServices.map((service: any) => (
                  <div key={service} className="flex items-center space-x-2">
                    <Checkbox
                      id={service}
                      checked={selectedServices.includes(service)}
                      onCheckedChange={() => handleServiceToggle(service)}
                    />
                    <Label htmlFor={service} className="text-sm">
                      {service}
                    </Label>
                  </div>
                ))}
              </div>
              {selectedServices.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedServices.map((service) => (
                    <Badge key={service} variant="secondary">
                      {service}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this assignment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Assignment"}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
